import { getConsentGate } from 'lib/consent/gate';
import { classifyRoute } from 'lib/consent/policy';

const UTM_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

const CLICK_FIELDS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'ttclid',
  'rdt_cid',
] as const;

const FIRST_TOUCH_FIELDS = [...UTM_FIELDS, 'landing_path', 'store'] as const;

export const CAMPAIGN_COOKIE = 'uh_campaign';
const CAMPAIGN_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

export type CampaignRecord = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  ttclid?: string;
  rdt_cid?: string;
  landing_path?: string;
  store?: string;
};

let memory: CampaignRecord | null = null;

export function parseCampaignSearch(search: string): Partial<CampaignRecord> {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const record: Partial<CampaignRecord> = {};
  for (const key of [...UTM_FIELDS, ...CLICK_FIELDS]) {
    const value = params.get(key);
    if (value) record[key] = value;
  }
  return record;
}

/** First-touch UTMs and landing stay. A new click id replaces last touch only. */
export function mergeCampaign(
  current: CampaignRecord | null,
  incoming: Partial<CampaignRecord>,
): CampaignRecord {
  const next: CampaignRecord = { ...(current ?? {}) };
  for (const key of FIRST_TOUCH_FIELDS) {
    if (!next[key] && incoming[key]) next[key] = incoming[key];
  }
  for (const key of CLICK_FIELDS) {
    if (incoming[key]) next[key] = incoming[key];
  }
  return next;
}

export function campaignHasFields(record: CampaignRecord | null): boolean {
  if (!record) return false;
  return Object.values(record).some((value) => Boolean(value));
}

function readCookie(): CampaignRecord | null {
  if (typeof document === 'undefined') return null;
  const row = document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${CAMPAIGN_COOKIE}=`));
  if (!row) return null;
  try {
    const parsed = JSON.parse(
      decodeURIComponent(row.slice(CAMPAIGN_COOKIE.length + 1)),
    ) as CampaignRecord;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Read the landing URL into memory. Private routes do not update memory and
 * do not return a record. The cookie is not written here.
 */
export function captureCampaignFromLocation(input: {
  search: string;
  pathname: string;
  store?: string;
}): CampaignRecord | null {
  if (classifyRoute(input.pathname) === 'private') return null;
  if (!memory) memory = readCookie() ?? {};
  memory = mergeCampaign(memory, {
    ...parseCampaignSearch(input.search),
    landing_path: input.pathname,
    store: input.store,
  });
  return campaignHasFields(memory) ? { ...memory } : null;
}

const CHECKOUT_PARAM_KEYS = [...UTM_FIELDS, ...CLICK_FIELDS] as const;

/** The five ad UTMs. Click ids are not included. */
export function campaignUtmParams(
  record: CampaignRecord | null | undefined,
): Array<[string, string]> {
  if (!record) return [];
  const params: Array<[string, string]> = [];
  for (const key of UTM_FIELDS) {
    const value = record[key];
    if (value) params.push([key, value]);
  }
  return params;
}

/** UTMs and click ids to copy onto a Shopify checkout URL. Landing path and store stay off it. */
export function campaignCheckoutParams(
  record: CampaignRecord | null | undefined,
): Array<[string, string]> {
  if (!record) return [];
  const params: Array<[string, string]> = [];
  for (const key of CHECKOUT_PARAM_KEYS) {
    const value = record[key];
    if (value) params.push([key, value]);
  }
  return params;
}

/** Pixel present and the current page still allows attribution. */
function attributionPixelLoaded(): boolean {
  if (typeof window === 'undefined' || !getConsentGate().attribution) return false;
  return (
    typeof (window as { TriplePixel?: unknown }).TriplePixel === 'function'
  );
}

/** Stored click ids for checkout. Empty until the attribution pixel is on the page. */
export function campaignCheckoutParamsIfAllowed(
  pathname?: string,
): Array<[string, string]> {
  if (!attributionPixelLoaded()) return [];
  return campaignCheckoutParams(getCampaign(pathname));
}

function setIfMissing(url: URL, key: string, value: string): void {
  if (!url.searchParams.has(key)) url.searchParams.set(key, value);
}

/** Fill checkout query keys this visit landed with when the current URL no longer has them. */
export function applyCampaignCheckoutParams(
  url: URL,
  pathname?: string,
): void {
  for (const [key, value] of campaignUtmParams(getCampaign(pathname))) {
    setIfMissing(url, key, value);
  }
  for (const [key, value] of campaignCheckoutParamsIfAllowed(pathname)) {
    setIfMissing(url, key, value);
  }
}

/** Record to attach to an event. Null on private routes and before capture. */
export function getCampaign(pathname?: string): CampaignRecord | null {
  if (pathname && classifyRoute(pathname) === 'private') return null;
  if (!campaignHasFields(memory)) return null;
  return { ...memory };
}

/** Persist after ads consent. No-op when there is nothing to store. */
export function persistCampaign(): void {
  if (typeof document === 'undefined') return;
  if (!campaignHasFields(memory)) return;
  const secure = window.location.protocol === 'https:' ? ';Secure' : '';
  document.cookie = `${CAMPAIGN_COOKIE}=${encodeURIComponent(JSON.stringify(memory))};path=/;max-age=${CAMPAIGN_MAX_AGE_SECONDS};SameSite=Lax${secure}`;
}

export function resetCampaignForTests(): void {
  memory = null;
}
